import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  HiShoppingCart,
  HiSearch,
  HiLocationMarker,
  HiPhone,
  HiArrowLeft,
  HiOfficeBuilding,
  HiClock,
} from "react-icons/hi";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API_BASE = "https://app.caaficare.so/api";

/** Check if pharmacy is currently open based on openingTime/closingTime (HH:mm format) */
const isPharmacyOpen = (pharmacy) => {
  const open = pharmacy?.openingTime || pharmacy?.opening_time;
  const close = pharmacy?.closingTime || pharmacy?.closing_time;
  if (!open || !close) return true; // No hours set = assume open 24/7

  const now = new Date();
  const [oh, om] = (open + "").split(":").map(Number);
  const [ch, cm] = (close + "").split(":").map(Number);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const openMinutes = (oh || 0) * 60 + (om || 0);
  const closeMinutes = (ch || 0) * 60 + (cm || 0);

  if (openMinutes <= closeMinutes) {
    return nowMinutes >= openMinutes && nowMinutes < closeMinutes;
  }
  return nowMinutes >= openMinutes || nowMinutes < closeMinutes;
};

/** Format time for display (e.g. "08:00" -> "8:00 AM") */
const formatTime = (t) => {
  if (!t) return "";
  const [h, m] = (t + "").split(":").map(Number);
  if (h === undefined || isNaN(h)) return t;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m || 0).padStart(2, "0")} ${ampm}`;
};

const PharmaciesList = () => {
  const navigate = useNavigate();
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 12;

  useEffect(() => {
    const fetchPharmacies = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(pageSize),
        });
        if (searchTerm.trim()) params.set("search", searchTerm.trim());

        const res = await axios.get(`${API_BASE}/pharmacies?${params.toString()}`);
        const data = res.data?.data || res.data || [];
        setPharmacies(Array.isArray(data) ? data : []);
        setTotalPages(res.data?.totalPages || Math.ceil((res.data?.total || 0) / pageSize) || 1);
      } catch (error) {
        console.error("Error fetching pharmacies:", error);
        toast.error("Failed to load pharmacies");
        setPharmacies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPharmacies();
  }, [page, searchTerm]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const PharmacyCard = ({ pharmacy }) => {
    const pharmacyId = pharmacy.id ?? pharmacy._id;
    const imageSrc = pharmacy.image || "/hero.jpeg";
    const open = isPharmacyOpen(pharmacy);
    const hasHours = (pharmacy?.openingTime || pharmacy?.opening_time) && (pharmacy?.closingTime || pharmacy?.closing_time);

    const handleClick = () => {
      if (!open) {
        toast.error("This pharmacy is currently closed. Orders are not available.");
        return;
      }
      navigate(`/pharmacy/${pharmacyId}`, { state: { pharmacy } });
    };

    return (
      <div
        onClick={handleClick}
        className={`bg-white rounded-2xl shadow-lg border-2 p-6 transition-all group ${
          open
            ? "border-gray-100 hover:shadow-xl hover:border-blue-200 cursor-pointer"
            : "border-gray-200 border-red-100 cursor-not-allowed opacity-90"
        }`}
      >
        <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 mb-4 relative">
          <img
            src={imageSrc}
            alt={pharmacy.name}
            className={`w-full h-full object-cover transition-transform duration-300 ${
              open ? "group-hover:scale-105" : ""
            }`}
            onError={(e) => (e.target.src = "/hero.jpeg")}
          />
          {hasHours && (
            <span
              className={`absolute top-2 right-2 px-3 py-1 rounded-lg font-bold text-xs ${
                open ? "bg-green-500/90 text-white" : "bg-red-500/90 text-white"
              }`}
            >
              {open ? "Open" : "Closed"}
            </span>
          )}
        </div>
        <h3 className="text-lg font-black text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
          {pharmacy.name}
        </h3>
        {pharmacy.location && (
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
            <HiLocationMarker className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <span className="line-clamp-1">Address: {pharmacy.location}</span>
          </div>
        )}
        {(pharmacy.city || pharmacy.district) && (
          <p className="text-sm text-gray-500 mb-2">
            City: {pharmacy.city}, {pharmacy.district}
          </p>
        )}
        

        <div className="mt-4 pt-4 border-t border-gray-100">
          <span
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm ${
              open
                ? "bg-blue-50 text-blue-700"
                : "bg-gray-100 text-gray-500"
            }`}
          >
            <HiShoppingCart className="w-4 h-4" />
            {open ? "View Products" : "Closed – Orders Unavailable"}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        onAppointmentClick={() => navigate("/hospitals")}
        onNurseAppointmentClick={() => navigate("/nurses-list")}
        onVideoConsultingClick={() => navigate("/doctors")}
        onApplyNurseClick={() => {}}
        onApplyDoctorClick={() => {}}
      />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-8">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-4 transition-colors"
            >
              <HiArrowLeft className="w-5 h-5" />
              <span className="text-sm font-bold">Back to Home</span>
            </button>
            <h1 className="text-4xl font-black text-gray-900 mb-2 flex items-center gap-3">
              <HiOfficeBuilding className="w-10 h-10 text-blue-600" />
              Pharmacies
            </h1>
            <p className="text-gray-500">
              Browse pharmacies and order products for pickup or delivery
            </p>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="mb-8">
            <div className="relative max-w-xl">
              <HiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search pharmacies by name, city, location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-2xl border-2 border-gray-200 bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all"
              />
            </div>
          </form>

          {loading ? (
            <div className="flex items-center justify-center min-h-[40vh]">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto" />
                <p className="mt-4 text-gray-500 font-bold">Loading pharmacies...</p>
              </div>
            </div>
          ) : pharmacies.length === 0 ? (
            <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
              <HiOfficeBuilding className="w-24 h-24 text-gray-300 mx-auto mb-6" />
              <h3 className="text-2xl font-black text-gray-900 mb-2">No Pharmacies Found</h3>
              <p className="text-gray-500">Try adjusting your search or check back later.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {pharmacies.map((pharmacy) => (
                  <PharmacyCard key={pharmacy.id ?? pharmacy._id} pharmacy={pharmacy} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-10">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="px-5 py-2.5 rounded-xl font-bold border-2 border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:border-blue-300"
                  >
                    Previous
                  </button>
                  <span className="px-5 py-2.5 rounded-xl font-bold bg-gray-100">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="px-5 py-2.5 rounded-xl font-bold border-2 border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:border-blue-300"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

    </div>
  );
};

export default PharmaciesList;
